<template>
    <div>

        <base-header class="header pb-8 pt-5 pt-lg-8 d-flex align-items-center no-padding-bottom">
            <span class="mask bg-gradient-danger opacity-8"></span>
        </base-header>


        <div class="container-fluid mt--7">
            <div class="row">
                <div class="col-xl-12 order-xl-1">
                    <card shadow type="secondary">
                        <div slot="header" class="bg-white border-0">
                            <div class="row align-items-center">
                                <div class="col-12">
                                    <h3 class="mb-0 text-uppercase">{{ this.$route.name }}</h3>
                                    <br>
                                    <span class="text-danger">
                                        Set up two-factor authentication.
                                    </span>
                                </div>
                                
                            </div>
                        </div>


                        <div class="row justify-content-center">
                            <div class="col-lg-5 col-md-7">
                                <div class="card bg-secondary shadow border-0">
                                    <div class="card-body px-lg-5 py-lg-5">

                                        <!-- Step 0: choose a method -->
                                        <div v-if="step === 'choose'">
                                            <div class="text-center text-muted mb-4">
                                                <h2>Set up two-factor authentication</h2>
                                                <strong>Choose how you'd like to verify it's you when you sign in</strong>
                                            </div>

                                            <div class="text-center">
                                                <base-button type="success" block class="mb-3" @click="chooseMethod('totp')">
                                                    <i class="ni ni-lock-circle-open"></i>&nbsp; Authenticator App
                                                </base-button>
                                                <base-button type="secondary" block @click="chooseMethod('email')">
                                                    <i class="ni ni-email-83"></i>&nbsp; Email Code
                                                </base-button>
                                            </div>
                                        </div>

                                        <!-- Step 1a: TOTP - scan + confirm -->
                                        <form role="form" v-else-if="step === 'totp'">
                                            <div class="text-center text-muted mb-4">
                                                <h2>Scan with your authenticator app</h2>
                                                <strong>Google Authenticator, Authy, or any TOTP app</strong>
                                            </div>

                                            <base-alert type="warning" dismissible v-if="showErrorBlade">
                                                <span class="alert-inner--icon"><i class="ni ni-sound-wave"></i></span>
                                                &nbsp;
                                                <span class="alert-inner--text" v-text="errorMessage"></span>
                                                <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                                                    <span aria-hidden="true">&times;</span>
                                                </button>
                                            </base-alert>

                                            <div class="text-center mb-3" v-if="isFetching">
                                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                Loading QR code...
                                            </div>

                                            <div class="text-center mb-3 qr-wrapper" v-else v-html="qrCodeSvg"></div>

                                            <p class="text-center text-muted mb-4" v-if="secret">
                                                Can't scan? Enter manually:
                                                <code>{{ secret }}</code>
                                            </p>

                                            <base-input class="input-group-alternative mb-3"
                                                        placeholder="6-digit code"
                                                        addon-left-icon="ni ni-lock-circle-open"
                                                        :valid="validCodeStatus"
                                                        maxlength="6"
                                                        @change="changeStatus"
                                                        @keyup.enter="confirmTotp()"
                                                        v-model="model.code">
                                            </base-input>

                                            <div class="text-center">
                                                <base-button type="success" class="my-4" @click="confirmTotp" v-if="! isLoading">
                                                    Confirm
                                                </base-button>
                                                <button class="btn btn-success" type="button" disabled v-if="isLoading">
                                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Please Wait...
                                                </button>
                                            </div>

                                            <div class="text-center">
                                                <a href="#" class="text-muted" @click.prevent="backToChoose">
                                                    Use a different method
                                                </a>
                                            </div>
                                        </form>

                                        <!-- Step 1b: Email - enter code sent to inbox -->
                                        <form role="form" v-else-if="step === 'email'">
                                            <div class="text-center text-muted mb-4">
                                                <h2>Check your email</h2>
                                                <strong>We sent a verification code to your email address</strong>
                                            </div>

                                            <base-alert type="warning" dismissible v-if="showErrorBlade">
                                                <span class="alert-inner--icon"><i class="ni ni-sound-wave"></i></span>
                                                &nbsp;
                                                <span class="alert-inner--text" v-text="errorMessage"></span>
                                                <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                                                    <span aria-hidden="true">&times;</span>
                                                </button>
                                            </base-alert>

                                            <base-input class="input-group-alternative mb-3"
                                                        placeholder="6-digit code"
                                                        addon-left-icon="ni ni-email-83"
                                                        :valid="validCodeStatus"
                                                        maxlength="6"
                                                        @change="changeStatus"
                                                        @keyup.enter="confirmEmail()"
                                                        v-model="model.code">
                                            </base-input>

                                            <div class="text-center">
                                                <base-button type="success" class="my-4" @click="confirmEmail" v-if="! isLoading">
                                                    Confirm
                                                </base-button>
                                                <button class="btn btn-success" type="button" disabled v-if="isLoading">
                                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Please Wait...
                                                </button>
                                            </div>

                                            <div class="text-center">
                                                <a href="#" class="text-muted mr-3 text-blue" @click.prevent="startEmailSetup" v-if="! isFetching">
                                                    Resend code
                                                </a>
                                                <a class="text-center mb-3" v-if="isFetching">
                                                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    Sending code ...
                                                </a>
                                                
                                                <a href="#" class="text-muted" @click.prevent="backToChoose">
                                                    Use a different method
                                                </a>
                                            </div>
                                        </form>

                                        <!-- Step 2: recovery codes -->
                                        <div v-else-if="step === 'recovery'">
                                            <div class="text-center text-muted mb-4">
                                                <h3>Save your recovery codes</h3>
                                                <small>Store these somewhere safe. Each one works once if you lose access to your {{ method === 'email' ? 'email' : 'authenticator app' }}.</small>
                                            </div>

                                            <div class="recovery-codes-box mb-4">
                                                <div class="row">
                                                    <div class="col-6 text-center mb-2" v-for="code in recoveryCodes" :key="code">
                                                        <code>{{ code }}</code>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="text-center">
                                                <base-button type="success" class="my-4" @click="finish">
                                                    {{ isLoading ? 'Please Wait...' : 'I\'ve saved these' }}
                                                </base-button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>



                    </card>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex';
    import axios from 'axios';
    import swal from 'sweetalert2';

    export default {
        name: 'MfaSetup',
        data() {
            return {
                model: {
                    code: ''
                },
                step: 'choose',       // choose | totp | email | recovery
                method: '',            // 'totp' | 'email' (set once chosen)
                secret: '',
                qrCodeSvg: '',
                recoveryCodes: [],
                isFetching: false,
                isLoading: false,
                showErrorBlade: false,
                errorMessage: '',
                validCodeStatus: null
            }
        },
        computed: {
            ...mapGetters(['getCpdAccessToken', 'getRootUrl'])
        },
        methods: {
            ...mapActions(['destroyAccessToken']),
            authHeader() {
                return { Authorization: `Bearer ${this.getCpdAccessToken}` };
            },
            chooseMethod(method) {
                this.method = method;
                this.model.code = '';
                this.showErrorBlade = false;
                this.validCodeStatus = null;
                if (method === 'totp') {
                    this.step = 'totp';
                    this.startTotpSetup();
                } else if (method === 'email') {
                    this.step = 'email';
                    this.startEmailSetup();
                }
            },
            backToChoose() {
                this.step = 'choose';
                this.method = '';
                this.model.code = '';
                this.showErrorBlade = false;
            },
            startTotpSetup() {
                this.isFetching = true;
                axios.get(this.getRootUrl+'/mfa/setup/totp', { headers: this.authHeader() })
                    .then((res) => {
                        this.isFetching = false;
                        this.secret = res.data.secret;
                        this.qrCodeSvg = res.data.qr_code_svg;
                    })
                    .catch(() => {
                        this.isFetching = false;
                        swal.fire("Network Failure!", 'Could not load your QR code. Kindly try again', "warning");
                    });
            },
            confirmTotp() {
                this.showErrorBlade = false;
                this.isLoading = true;
                if (! this.model.code || this.model.code.length !== 6) {
                    this.isLoading = false;
                    return this.showError('Enter the 6-digit code from your authenticator app');
                }
                axios.post(this.getRootUrl+'/mfa/setup/totp/confirm', { code: this.model.code }, { headers: this.authHeader() })
                    .then((res) => {
                        this.isLoading = false;
                        this.recoveryCodes = res.data.recovery_codes;
                        this.step = 'recovery';
                    })
                    .catch((err) => {
                        this.isLoading = false;
                        this.validCodeStatus = false;
                        this.showError(this.errMsg(err, 'Invalid code. Kindly try again'));
                    });
            },
            startEmailSetup() {
                this.isFetching = true;
                axios.post(this.getRootUrl+'/mfa/setup/email/start', {}, { headers: this.authHeader() })
                    .then(() => {
                        this.isFetching = false;
                    })
                    .catch(() => {
                        this.isFetching = false;
                        swal.fire("Network Failure!", 'Could not send a code to your email. Kindly try again', "warning");
                    });
            },
            confirmEmail() {
                this.showErrorBlade = false;
                this.isLoading = true;
                if (! this.model.code || this.model.code.length !== 6) {
                    this.isLoading = false;
                    return this.showError('Enter the 6-digit code sent to your email');
                }
                axios.post(this.getRootUrl+'/mfa/setup/email/confirm', { code: this.model.code }, { headers: this.authHeader() })
                    .then((res) => {
                        this.isLoading = false;
                        this.recoveryCodes = res.data.recovery_codes;
                        this.step = 'recovery';
                    })
                    .catch((err) => {
                        this.isLoading = false;
                        this.validCodeStatus = false;
                        this.showError(this.errMsg(err, 'Invalid code. Kindly try again'));
                    });
            },
            finish() {
                this.isLoading = true;
                return this.destroyAccessToken(); // remove pending token
                // return window.location.href = '/login';
            },
            errMsg(err, fallback) {
                return (err.response && err.response.data && err.response.data.message) || fallback;
            },
            showError(msg) {
                this.showErrorBlade = false;
                this.errorMessage = '';
                this.showErrorBlade = true;
                this.errorMessage = msg;
            },
            changeStatus() {
                this.showErrorBlade = false;
                this.validCodeStatus = this.model.code && this.model.code.length === 6 ? true : false;
            }
        }
    }
</script>

<style scoped>
    .qr-wrapper >>> svg {
        max-width: 200px;
    }
    .recovery-codes-box {
        background: #f6f9fc;
        border-radius: 0.375rem;
        padding: 1rem;
    }
</style>

